import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/useAuth";
import { useAuthConfig } from "@/api/queries";
import { ApiError } from "@/api/client";

interface LocationState {
  from?: { pathname: string };
}

const inputClass =
  "block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus-visible:outline focus-visible:ring-2 focus-visible:ring-slate-500";

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return "E-Mail oder Passwort ist falsch.";
    if (err.status === 409) return "Für diese E-Mail gibt es schon ein Konto.";
    if (err.status === 429)
      return "Zu viele Fehlversuche. Bitte später erneut versuchen.";
    return err.problem.detail ?? err.problem.title;
  }
  return "Anmeldung fehlgeschlagen.";
}

export function LoginPage() {
  const { login, loginWithPassword, register, isAuthenticated } = useAuth();
  const authConfig = useAuthConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from?.pathname ?? "/";

  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [signupCode, setSignupCode] = useState("");
  const [userId, setUserId] = useState("creator-demo");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) void navigate(from, { replace: true });
  }, [isAuthenticated, from, navigate]);

  const isFake = authConfig.data?.mode === "fake";
  const signupEnabled = authConfig.data?.signup_enabled ?? false;
  const codeRequired = authConfig.data?.signup_code_required ?? false;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (isFake) {
        await login(userId.trim(), ["creator"]);
      } else if (mode === "register") {
        await register({
          email: email.trim(),
          password,
          display_name: displayName.trim() || undefined,
          signup_code: codeRequired ? signupCode.trim() : undefined,
        });
      } else {
        await loginWithPassword(email.trim(), password);
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm space-y-4 rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
        aria-label={mode === "register" ? "Registrierung" : "Anmeldung"}
      >
        <h1 className="text-xl font-semibold text-slate-800">
          {mode === "register" ? "Konto erstellen" : "Anmelden"}
        </h1>
        <p className="text-sm text-slate-500">
          Zauber.app — personalisierte Kinderbücher.
        </p>

        {isFake ? (
          <div className="space-y-1">
            <label
              htmlFor="user-id"
              className="block text-sm font-medium text-slate-700"
            >
              Benutzer-ID (Test-Modus)
            </label>
            <input
              id="user-id"
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              required
              className={inputClass}
            />
          </div>
        ) : (
          <>
            <div className="space-y-1">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-slate-700"
              >
                E-Mail
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={inputClass}
              />
            </div>
            <div className="space-y-1">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-slate-700"
              >
                Passwort
              </label>
              <input
                id="password"
                type="password"
                autoComplete={
                  mode === "register" ? "new-password" : "current-password"
                }
                minLength={mode === "register" ? 10 : undefined}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={inputClass}
              />
              {mode === "register" ? (
                <p className="text-xs text-slate-500">
                  Mindestens 10 Zeichen. Es gibt keine
                  Passwort-Wiederherstellung.
                </p>
              ) : null}
            </div>
            {mode === "register" ? (
              <>
                <div className="space-y-1">
                  <label
                    htmlFor="display-name"
                    className="block text-sm font-medium text-slate-700"
                  >
                    Name (optional)
                  </label>
                  <input
                    id="display-name"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className={inputClass}
                  />
                </div>
                {codeRequired ? (
                  <div className="space-y-1">
                    <label
                      htmlFor="signup-code"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Einladungscode
                    </label>
                    <input
                      id="signup-code"
                      type="text"
                      value={signupCode}
                      onChange={(e) => setSignupCode(e.target.value)}
                      required
                      className={inputClass}
                    />
                  </div>
                ) : null}
              </>
            ) : null}
          </>
        )}

        {error ? (
          <p
            className="rounded-md bg-red-50 p-2 text-sm text-red-700"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting || authConfig.isLoading}
          className="inline-flex min-h-touch w-full items-center justify-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60 focus-visible:outline focus-visible:ring-2 focus-visible:ring-slate-500"
        >
          {submitting
            ? "Bitte warten…"
            : mode === "register"
              ? "Konto erstellen"
              : "Anmelden"}
        </button>

        {!isFake && signupEnabled ? (
          <button
            type="button"
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setError(null);
            }}
            className="w-full text-center text-sm text-slate-600 underline hover:text-slate-900"
          >
            {mode === "login"
              ? "Noch kein Konto? Registrieren"
              : "Schon ein Konto? Anmelden"}
          </button>
        ) : null}
      </form>
    </div>
  );
}
